import { useState } from 'react'
import { cn } from '@/lib/utils'
import { VAULT_CATEGORY_LABELS } from '@/lib/vault-document.constants'

/**
 * Renders a tool part in the `approval-requested` state — the AI SDK's
 * human-in-the-loop pause. A tool flagged `needsApproval` on the server
 * (driven by `audit.requiresHuman`) reaches the client as a
 * `tool-{name}` part with `state: 'approval-requested'`; the user's
 * Approve/Cancel choice is dispatched on `screenata:tool-approval`,
 * which `chat.tsx` feeds back into the SDK to resume or abort the call.
 */

interface ToolApprovalMeta {
  title: string | ((args: Record<string, any>) => string)
  description: string | ((args: Record<string, any>) => string)
  tone?: 'primary' | 'warning' | 'danger'
  approveLabel?: string
  rejectLabel?: string
  /** Optional business status for the object being changed. */
  statusLabel?: (args: Record<string, any>) => string | null | undefined
  /** Optional one-line summary pulled from the call args. */
  detail?: (args: Record<string, any>) => string | null | undefined
}

/**
 * Per-tool copy for the approval prompt. Tools not listed fall back to a
 * humanized title and a generic description.
 */
const TOOL_APPROVAL_META: Record<string, ToolApprovalMeta> = {
  evidenceSubmitForReview: {
    title: (a) => `Submit ${testLabel(a)} for review`,
    description: 'This will send the test evidence to a reviewer.',
    approveLabel: 'Submit for review',
    statusLabel: () => 'Pending review',
    detail: (a) => a.reviewerNotes ?? null,
  },
  evidenceWithdrawSubmission: {
    title: (a) => `Withdraw ${testLabel(a)} submission`,
    description:
      'This will move the test back to In Progress so evidence can be changed.',
    tone: 'warning',
    approveLabel: 'Withdraw',
    statusLabel: () => 'In progress',
  },
  evidenceApproveWithStatusSync: {
    title: (a) =>
      `Approve evidence submission ${pickArg(a.submissionId)}`.trim(),
    description:
      'This will approve the submission and mark the test as Approved.',
    approveLabel: 'Approve',
    statusLabel: () => 'Approved',
    detail: (a) => a.comments ?? null,
  },
  checksRemedyExecute: {
    title: (a) => `Apply remediation ${checkLabel(a)}`.trim(),
    description: 'This will apply the validated remediation plan.',
    tone: 'warning',
    approveLabel: 'Apply remediation',
    statusLabel: () => 'Remediation',
  },
  checksRemedyUndo: {
    title: (a) => `Undo remediation ${checkLabel(a)}`.trim(),
    description: 'This will reverse the latest applied remediation.',
    tone: 'warning',
    approveLabel: 'Undo',
    statusLabel: () => 'Rollback',
  },
  findingsAcceptRisk: {
    title: (a) => `Accept risk for ${pickArg(a.findingKey, 'finding')}`,
    description: 'This will mark the finding as an accepted risk.',
    tone: 'danger',
    approveLabel: 'Accept risk',
    statusLabel: () => 'Accepted risk',
    detail: (a) => a.reason ?? null,
  },
  findingsFalsePositive: {
    title: (a) =>
      `Mark ${pickArg(a.findingKey, 'finding')} as false positive`,
    description: 'This will close the finding as a false positive.',
    tone: 'warning',
    approveLabel: 'Mark false positive',
    statusLabel: () => 'False positive',
    detail: (a) => a.reason ?? null,
  },
  risksUpdate: {
    title: (a) => `Update risk ${a.riskId ?? ''}`.trim(),
    description: (a) => describeRiskUpdate(a),
    approveLabel: 'Update risk',
  },
  runbookExecutionApprove: {
    title: (a) => `Approve procedure gate ${pickArg(a.executionId)}`.trim(),
    description: 'This will resume the procedure execution.',
    approveLabel: 'Approve & resume',
    statusLabel: () => 'Gate approval',
    detail: (a) => a.comment ?? null,
  },
  runbookExecutionReject: {
    title: (a) => `Reject procedure gate ${pickArg(a.executionId)}`.trim(),
    description: 'This will stop the procedure execution.',
    tone: 'danger',
    approveLabel: 'Reject',
    statusLabel: () => 'Gate approval',
    detail: (a) => a.reason ?? a.comment ?? null,
  },
  generatePolicy: {
    title: (a) => `Regenerate policy ${policyLabel(a)}`.trim(),
    description: 'This replaces the current policy with a new draft.',
    approveLabel: 'Regenerate',
    statusLabel: () => 'Draft',
  },
  policiesGenerate: {
    title: (a) => `Regenerate policy ${policyLabel(a)}`.trim(),
    description:
      'This overwrites the current policy draft and rewrites linked claim rows.',
    tone: 'warning',
    approveLabel: 'Regenerate',
    statusLabel: () => 'Draft',
  },
  apiCheckBind: {
    title: (a) => `Bind check ${pickArg(a.checkId, 'to test')}`.trim(),
    description: (a) =>
      `This links the automated check to ${testLabel(a)} so it can collect evidence.`,
    approveLabel: 'Bind check',
  },
  risksCreate: {
    title: (a) => `Create risk ${a.title ? `"${a.title}"` : ''}`.trim(),
    description: (a) =>
      `This adds a new ${formatStatusLabel(a.riskType) ?? 'manual'} risk to the register.`,
    approveLabel: 'Create risk',
  },
  testsAssign: {
    title: (a) => `Assign ${testLabel(a)}`,
    description: (a) =>
      `This assigns the test to ${pickArg(a.email, pickArg(a.userId, 'the selected user'))}.`,
    approveLabel: 'Assign',
  },
  testsSetDueDate: {
    title: (a) => `Set due date on ${testLabel(a)}`,
    description: (a) =>
      a.dueDate
        ? `This sets the due date to ${a.dueDate}.`
        : 'This clears the due date.',
    approveLabel: 'Set due date',
  },
}

const APPROVAL_BLOCK_CLASS =
  'my-2 w-full max-w-lg rounded-md border border-border px-3 py-2.5 text-[13px] leading-[1.5] text-text-body last:mb-0'

const APPROVAL_PRIMARY_BUTTON = {
  primary: 'bg-primary hover:bg-primary/90',
  warning: 'bg-primary hover:bg-primary/90',
  danger: 'bg-status-red text-white hover:bg-destructive-emphasis',
} as const

/** Minimal inline prompt — reads like assistant text, not a card. */
function ApprovalInline({
  title,
  hint,
  tone,
  decided,
  approved,
  approveLabel,
  rejectLabel,
  onRespond,
}: {
  title: string
  hint?: string | null
  tone: keyof typeof APPROVAL_PRIMARY_BUTTON
  decided: boolean
  approved: boolean | null
  approveLabel: string
  rejectLabel: string
  onRespond: (approved: boolean) => void
}) {
  const showDecided = decided && approved !== null

  return (
    <div data-approval-container className={APPROVAL_BLOCK_CLASS}>
      <p className='font-medium text-text-secondary'>{title}</p>
      {hint ? (
        <p className='mt-0.5 text-[12px] text-text-subtle'>{hint}</p>
      ) : null}
      {showDecided ? null : (
        <div className='mt-2.5 flex items-center justify-end gap-1.5'>
          <button
            type='button'
            onClick={() => onRespond(false)}
            disabled={decided}
            className='h-7 rounded-md border border-border-divider bg-card px-2.5 text-[11px] font-medium text-text-body transition-colors hover:border-border-divider-hover hover:bg-card disabled:pointer-events-none disabled:opacity-50'
          >
            {rejectLabel}
          </button>
          <button
            type='button'
            onClick={() => onRespond(true)}
            disabled={decided}
            className={cn(
              'h-7 rounded-md px-2.5 text-[11px] font-semibold text-primary-foreground transition-colors disabled:pointer-events-none disabled:opacity-50',
              APPROVAL_PRIMARY_BUTTON[tone]
            )}
          >
            {approveLabel}
          </button>
        </div>
      )}
    </div>
  )
}

function humanizeToolName(toolName: string): string {
  return toolName
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatStatusLabel(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim().length === 0) return null
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function pickArg(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : fallback
}

function testLabel(args: Record<string, any>): string {
  return pickArg(args.taskCode, pickArg(args.testId, 'test'))
}

function checkLabel(args: Record<string, any>): string {
  return pickArg(args.testId, pickArg(args.checkId))
}

function policyLabel(args: Record<string, any>): string {
  return pickArg(
    args.policyName,
    pickArg(args.policyTitle, pickArg(args.policySlug, pickArg(args.slug)))
  )
}

function describeRiskUpdate(args: Record<string, any>): string {
  const changes = [
    args.status ? `set status to ${formatStatusLabel(args.status)}` : null,
    args.treatment
      ? `set treatment to ${formatStatusLabel(args.treatment)}`
      : null,
    args.likelihood
      ? `set likelihood to ${formatStatusLabel(args.likelihood)}`
      : null,
    args.impact ? `set impact to ${formatStatusLabel(args.impact)}` : null,
    args.targetDate ? `set target date to ${args.targetDate}` : null,
    args.reviewDate ? `set review date to ${args.reviewDate}` : null,
  ].filter(Boolean)

  return changes.length > 0
    ? `This will ${changes.join(', ')}.`
    : 'This will update the risk register.'
}

interface ApprovalRequestCardProps {
  part: any
}

export function ApprovalRequestCard({ part }: ApprovalRequestCardProps) {
  const [localDecided, setLocalDecided] = useState(false)

  const approval = part.approval
  const toolType = String(part.type ?? '')
  const toolName = toolType.startsWith('tool-')
    ? toolType.slice(5)
    : toolType
  const args: Record<string, any> = part.input ?? part.args ?? {}

  // The SDK reports the user's decision back through `part.approval.approved`
  // once the `addToolApprovalResponse` round-trip completes. Until then, we
  // optimistically flip `localDecided` so the buttons disable immediately
  // after the click. The prompt persists across all post-decision states
  // (`approval-responded` -> `output-available` / `output-denied`) so the
  // user always sees a record of what they chose.
  const partState = String(part.state ?? '')
  const sdkApproved: boolean | null =
    typeof approval?.approved === 'boolean' ? approval.approved : null
  const isResponded =
    sdkApproved !== null ||
    partState === 'approval-responded' ||
    partState === 'output-denied' ||
    partState === 'output-error'
  const decided = isResponded || localDecided

  const respond = (approved: boolean) => {
    if (decided) return
    setLocalDecided(true)
    window.dispatchEvent(
      new CustomEvent('screenata:tool-approval', {
        detail: { approvalId: approval?.id, approved },
      })
    )
  }

  const isVaultDocSave =
    toolName === 'documentsSaveDraft' ||
    (toolName === 'vault-document' && args.action === 'saveDraft') ||
    (toolName === 'documents' && args.action === 'saveDraft')

  if (isVaultDocSave) {
    const docTitle = args.title ?? 'Untitled Document'
    const category = args.category ?? 'OTHER'
    const categoryLabel = VAULT_CATEGORY_LABELS[category] ?? category
    return (
      <ApprovalInline
        title={`Save draft: ${docTitle}`}
        hint={categoryLabel}
        tone='primary'
        decided={decided}
        approved={isResponded ? (sdkApproved ?? true) : null}
        approveLabel='Saved'
        rejectLabel='Cancelled'
        onRespond={respond}
      />
    )
  }

  const meta = TOOL_APPROVAL_META[toolName]
  const title =
    typeof meta?.title === 'function'
      ? meta.title(args)
      : (meta?.title ?? humanizeToolName(toolName))
  const description =
    typeof meta?.description === 'function'
      ? meta.description(args)
      : (meta?.description ??
        'This action requires your approval before it runs.')
  const approveLabel = meta?.approveLabel ?? 'Approve'
  const rejectLabel = meta?.rejectLabel ?? 'Cancel'
  const tone = meta?.tone ?? 'primary'

  return (
    <ApprovalInline
      title={title}
      hint={description}
      tone={tone}
      decided={decided}
      approved={isResponded ? (sdkApproved ?? true) : null}
      approveLabel={approveLabel}
      rejectLabel={rejectLabel}
      onRespond={respond}
    />
  )
}
